import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SettingsPage() {
  const [customFields, setCustomFields] = useState([]);
  const [newField, setNewField] = useState({
    field_name: '',
    field_type: 'text',
    options: '',
    is_required: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-fields`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    if (!newField.field_name.trim()) {
      setMessage({ type: 'error', text: 'Field name is required' });
      return;
    }

    setSaving(true);
    try {
      const fieldData = {
        field_name: newField.field_name.trim(),
        field_type: newField.field_type,
        options: newField.field_type === 'select' 
          ? newField.options.split(',').map(o => o.trim()).filter(o => o)
          : null,
        is_required: newField.is_required
      };

      await axios.post(`${API_URL}/api/custom-fields`, fieldData);
      setMessage({ type: 'success', text: 'Custom field added successfully!' });
      setNewField({ field_name: '', field_type: 'text', options: '', is_required: false });
      fetchCustomFields();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error adding custom field' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (fieldName) => {
    if (!window.confirm(`Are you sure you want to delete the field "${fieldName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/custom-fields/${encodeURIComponent(fieldName)}`);
      setMessage({ type: 'success', text: 'Custom field deleted successfully!' });
      fetchCustomFields();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error deleting custom field' 
      });
    }
  };

  if (loading) {
    return (
      <div data-testid="settings-loading" className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in max-w-4xl">
      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <AlertCircle className="w-5 h-5" />
          {message.text}
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="ml-auto text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Add Custom Field */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-4">Add Custom Field</h2>
        <p className="text-sm text-zinc-500 mb-4">
          Create custom fields to add additional information to your invoices. These fields will appear in the invoice form.
        </p>

        <form onSubmit={handleAddField} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Field Name *
              </label>
              <input
                data-testid="new-field-name-input"
                type="text"
                value={newField.field_name}
                onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
                placeholder="e.g., Warranty Period"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Field Type
              </label>
              <select
                data-testid="new-field-type-select"
                value={newField.field_type}
                onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="select">Dropdown</option>
              </select>
            </div>

            {newField.field_type === 'select' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Options (comma separated)
                </label>
                <input
                  type="text"
                  value={newField.options}
                  onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                data-testid="new-field-required-checkbox"
                type="checkbox"
                id="is_required"
                checked={newField.is_required}
                onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                className="w-4 h-4 text-emerald-600 border-zinc-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="is_required" className="text-sm text-zinc-700">
                Required field
              </label>
            </div>
          </div>

          <button
            type="submit"
            data-testid="add-field-btn"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium text-sm disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {saving ? 'Adding...' : 'Add Field'}
          </button>
        </form>
      </div>

      {/* Existing Custom Fields */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-4">Custom Fields</h2>

        {customFields.length === 0 ? (
          <p className="text-zinc-500 text-sm">
            No custom fields added yet. Add your first custom field above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-zinc-700">Field Name</th>
                  <th className="px-4 py-3 text-left font-medium text-zinc-700">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-zinc-700">Options</th>
                  <th className="px-4 py-3 text-left font-medium text-zinc-700">Required</th>
                  <th className="px-4 py-3 text-center font-medium text-zinc-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customFields.map((field) => (
                  <tr key={field.field_name} data-testid={`custom-field-${field.field_name}`} className="border-b border-zinc-100">
                    <td className="px-4 py-3 font-medium text-zinc-900">{field.field_name}</td>
                    <td className="px-4 py-3 text-zinc-600 capitalize">{field.field_type}</td>
                    <td className="px-4 py-3 text-zinc-600">
                      {field.options ? field.options.join(', ') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {field.is_required ? (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Yes</span>
                      ) : (
                        <span className="px-2 py-1 bg-zinc-100 text-zinc-600 rounded text-xs font-medium">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        data-testid={`delete-field-${field.field_name}`}
                        onClick={() => handleDeleteField(field.field_name)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Delete Field"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">How Custom Fields Work</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Custom fields appear in the "Additional Fields" section when creating an invoice</li>
          <li>Fields marked as required must be filled before saving an invoice</li>
          <li>Dropdown fields allow you to predefine options for consistent data entry</li>
          <li>Custom field data is included in exports and can be imported from Excel files</li>
        </ul>
      </div>
    </div>
  );
}

export default SettingsPage;
